// Small DOM helpers shared by the UI modules.

import { t } from '../i18n';

export const $ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document): T => {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error(`Missing element ${sel}`);
  return el;
};

export const $$ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  Array.from(root.querySelectorAll<T>(sel));

export function esc(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

let toastTimer = 0;
export function toast(msg: string, ms = 2400): void {
  const el = $('#toast');
  el.textContent = msg;
  el.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (el.hidden = true), ms);
}

/** Builds a segmented control; returns an updater that marks the active option. */
export function segmented<V extends string>(
  host: HTMLElement,
  options: readonly V[],
  label: (v: V) => string,
  onPick: (v: V) => void,
  title?: (v: V) => string,
): (active: V | null, disabled?: (v: V) => boolean) => void {
  host.innerHTML = '';
  const buttons = options.map((v) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.dataset.v = v;
    b.addEventListener('click', () => onPick(v));
    host.appendChild(b);
    return b;
  });
  return (active, disabled) => {
    buttons.forEach((b, i) => {
      const v = options[i];
      const txt = label(v);
      if (txt.startsWith('<svg')) {
        if (b.dataset.html !== txt) {
          b.innerHTML = txt;
          b.dataset.html = txt;
        }
      } else if (b.textContent !== txt) b.textContent = txt;
      const tt = title ? title(v) : '';
      if (b.title !== tt) b.title = tt;
      b.classList.toggle('on', v === active);
      b.disabled = !!disabled?.(v);
    });
  };
}

export function setText(el: HTMLElement, s: string): void {
  if (el.textContent !== s) el.textContent = s;
}

export function openModal(id: string): void {
  $(id).hidden = false;
}

export function closeModal(id: string): void {
  $(id).hidden = true;
}

export function anyModalOpen(): boolean {
  return $$('.modal').some((m) => !m.hidden);
}

/** Generic message dialog; resolves with the index of the chosen action (or -1 when closed). */
export function message(title: string, bodyHtml: string, actions: { label: string; primary?: boolean }[]): Promise<number> {
  const modal = $('#messageModal');
  $('#msgTitle').textContent = title;
  $('#msgBody').innerHTML = bodyHtml;
  const host = $('#msgActions');
  host.innerHTML = '';
  return new Promise((resolve) => {
    const done = (i: number) => {
      modal.hidden = true;
      resolve(i);
    };
    actions.forEach((a, i) => {
      const b = document.createElement('button');
      b.textContent = a.label;
      if (a.primary) b.className = 'primary';
      b.addEventListener('click', () => done(i));
      host.appendChild(b);
    });
    const close = modal.querySelector<HTMLButtonElement>('[data-close]')!;
    close.onclick = () => done(-1);
    modal.hidden = false;
    (host.querySelector('.primary') as HTMLButtonElement | null)?.focus();
  });
}

export function fmtSigned(n: number): string {
  return (n > 0 ? '+' : '') + n;
}

export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function tr(key: string, params?: Record<string, string | number>): string {
  return t(key, params);
}
